import { ref } from 'vue';

/**
 * useSgaRenovacionMembresiaPago
 *
 * Handles all payment processing logic for SGA membership renewal modal.
 * Mirrors useSgaRenovacionPago (abonos) but targets the membership renewal
 * endpoints and omits seat/zone data (a renewed membership has no seat).
 *
 * @param {Object} context - Context with Vue instance dependencies
 * @param {Object} context.$store - Vuex store
 * @param {Object} context.$config - Config object with getVuexAlias method
 * @param {Function} context.$emit - Emit function from component
 * @param {Object} context.$toast - Toast service
 * @param {Function} context.getCookie - Cookie getter (e.g. (name) => this.$cookies.get(name))
 * @param {Object} options - Configuration options
 * @param {Ref} options.selectedPeriod - Currently selected renewal period
 * @param {String|Number} options.abonado_id - Subscriber ID
 * @param {String} options.sales_category_id - Sales category ID
 * @param {Number} options.gastos_gestion - Management fees
 * @param {Ref} options.refs - Component template refs
 *
 * @returns {Object} Payment state, data, and methods
 */
export default function useSgaRenovacionMembresiaPago(context, options) {
    const {
        $store,
        $config,
        $emit,
        $toast,
        $t,
        getCookie
    } = context;

    const {
        selectedPeriod,
        abonado_id,
        sales_category_id,
        gastos_gestion,
        refs
    } = options;

    // Payment state machine
    const paymentState = ref('selection'); // 'selection' | 'payment' | 'success'
    const paymentData = ref({
        jet_id: null,
        session_id: null,
        cart_id: null,
        total_price: null,
        tpv_order: null
    });
    const paymentError = ref(null);
    const paymentProcessing = ref(false);
    const paymentChallengeUrl = ref(null);
    const paymentCheckInterval = ref(null);
    const paymentCheckInFlight = ref(false);
    // Polling 3DS acotado: tras N intentos sin confirmación se detiene y se
    // muestra un error terminal (el usuario puede cancelar/reintentar). Sin
    // tope, un challenge abandonado mantiene el spinner para siempre.
    const paymentCheckAttempts = ref(0);
    const MAX_POLLING_ATTEMPTS = 20; // 20 * 3s = 60s máximo esperando el 3DS
    // Autoenvío Redsys: el timeout se guarda para poder cancelarlo si el
    // usuario cierra el modal antes de que se dispare, y el flag evita que
    // un submit pendiente se ejecute tras la cancelación del carrito.
    const redsysSubmitTimeout = ref(null);
    const redsysCancelled = ref(false);
    // Cancelación de la operación de pago: se marca al cancelar (botón o
    // desmontaje) y hace que las respuestas de polling/completePayment que
    // lleguen después se ignoren (no mostrar éxito para un carrito borrado).
    const paymentCancelled = ref(false);
    // Promesa de cancelación en curso: cancelPayment la comparte entre
    // llamadas (X dispara close + closed) para que el segundo llamador pueda
    // encadenar .catch() sin TypeError.
    let cancelInFlight = null;
    // Guard de concurrencia de confirmRenovacion: dos clics rápidos no deben
    // crear dos carritos de renovación para la misma membresía.
    const confirmInFlight = ref(false);
    const cartData = ref(null);
    const successData = ref(null);
    const loading = ref(false);
    const error = ref(null);

    /**
     * Confirm renewal: POST to create cart, apply discounts, then show payment or redirect.
     */
    const confirmRenovacion = async() => {
        if (confirmInFlight.value) {
            return;
        }
        confirmInFlight.value = true;
        loading.value = true;
        error.value = null;

        try {
            // Renovación de membresías: SGA no aplica promociones ni
            // descuentos en este flujo; el payload solo lleva categoría,
            // periodo y cookies de tracking.
            const payload = {
                abonado_id,
                period_id: selectedPeriod.value.id,
                salesCategoryId: sales_category_id || selectedPeriod.value.sales_category_id
            };

            // Include tracking cookies if present (same as abono flow)
            const fbp = getCookie ? getCookie('_fbp') : null;
            const fbc = getCookie ? getCookie('_fbc') : null;
            if (fbp && fbc) {
                payload.fbp = fbp;
                payload.fbc = fbc;
            }

            const response = await $store.dispatch(
                $config.getVuexAlias('sga.profile.membresias.renovacion'),
                payload
            );

            cartData.value = response.data;

            // Route to payment based on response
            handlePaymentRouting(cartData.value);

        } catch (err) {
            handleError(err);
        } finally {
            loading.value = false;
            confirmInFlight.value = false;
        }
    };

    /**
     * Route to Redsys or Paycomet based on cart response.
     */
    const handlePaymentRouting = cartDataResponse => {
        if (cartDataResponse.formulario) {
            // Redsys: inject form and auto-submit (user will be redirected out)
            handleRedsysPayment(
                cartDataResponse.formulario,
                cartDataResponse.session_id,
                cartDataResponse.cart_id,
                cartDataResponse.tpv_order,
                cartDataResponse.total_price
            );
        } else if (cartDataResponse.jet_id) {
            // Paycomet: show payment form inside modal
            handlePaycometPayment(
                cartDataResponse.jet_id,
                cartDataResponse.session_id,
                cartDataResponse.cart_id,
                cartDataResponse.total_price,
                cartDataResponse.tpv_order
            );
        } else {
            // Respuesta sin método de pago: el carrito ya se creó en SGA.
            // Se conserva el cart_id y se pasa a estado payment (cancelable)
            // para que el cierre lo borre — si no, quedaría huérfano sin
            // pantalla desde la que cancelarlo.
            paymentData.value = {
                ...paymentData.value,
                session_id: cartDataResponse.session_id,
                cart_id: cartDataResponse.cart_id,
                tpv_order: cartDataResponse.tpv_order
            };
            paymentState.value = 'payment';
            error.value = $t('perfil_sga.error_renovacion');
        }
    };

    /**
     * Paycomet payment: show form inside modal.
     * Do NOT redirect to CompraFinalizada until successful payment.
     */
    const handlePaycometPayment = (jet_id, session_id, cart_id, total_price, tpv_order) => {
        paymentState.value = 'payment';
        paymentCancelled.value = false;
        paymentData.value = {
            jet_id,
            session_id,
            cart_id,
            total_price,
            tpv_order
        };
        paymentError.value = null;
    };

    /**
     * Redsys payment: inject form HTML, wait, auto-submit.
     * User will be redirected to Redsys TPV.
     */
    const handleRedsysPayment = (formulario, session_id, cart_id, tpv_order, total_price) => {
        // Store payment metadata for potential cleanup or tracking
        paymentData.value = {
            ...paymentData.value,
            session_id,
            cart_id,
            tpv_order,
            // El total se conserva para el resumen de pago: sin él,
            // formattedTotalPrice devuelve vacío en la vista de pago.
            total_price: total_price ?? paymentData.value.total_price
        };

        // El modal pasa a estado de pago: closeModal debe poder cancelar el
        // carrito si el usuario cierra la ventana antes de volver del TPV.
        paymentState.value = 'payment';
        paymentCancelled.value = false;
        redsysCancelled.value = false;

        // Inject form
        // Redsys requires raw HTML form injection — this is the only viable approach
        // because the TPV expects a <form> with hidden fields to auto-submit.
        // The HTML comes from our own backend (trusted proxy), which constructs
        // and sanitizes the form fields before sending them to the client.
        if (refs.redsysFormContainer) {
            refs.redsysFormContainer.innerHTML = formulario;

            // Wait 100ms then submit. El timeout se guarda para cancelarlo si
            // el usuario cierra el modal antes: cancelPayment() marca
            // redsysCancelled y limpia el timeout, así el POST al TPV nunca
            // compite con el DELETE del carrito.
            redsysSubmitTimeout.value = setTimeout(() => {
                redsysSubmitTimeout.value = null;
                if (redsysCancelled.value) {
                    return;
                }
                const form = refs.redsysFormContainer.querySelector('form');
                if (form) {
                    // Browser redirects to TPV
                    form.submit();
                } else {
                    error.value = $t('perfil_sga.error_renovacion');
                }
            }, 100);
        }
    };

    /**
     * Handle payToken emitted from FormularioPagoComponent.
     * Sends jetToken to backend to process payment.
     */
    const handlePayToken = jetToken => {
        if (paymentProcessing.value) {
            return;
        }

        paymentProcessing.value = true;
        paymentCancelled.value = false;
        paymentError.value = null;

        const params = {
            filters: {},
            session_id: paymentData.value.session_id,
            jetToken: jetToken,
            tipo_venta: 'sga'
        };

        $store.dispatch($config.getVuexAlias('front.procesar-pago-paycomet'), params)
            .then(res => {
                // Operación cancelada mientras la petición estaba en vuelo:
                // ignorar la respuesta (el carrito ya no existe).
                if (paymentCancelled.value) {
                    return;
                }

                // Extract tpv_order from Paycomet response if available
                if (res.data?.order) {
                    paymentData.value.tpv_order = res.data.order;
                }

                // Normalizar la forma de la respuesta: Paycomet puede devolver
                // un payload incompleto; sin `response` no hay estado que
                // interpretar y se trata como error de pago.
                const response = res.data?.response;
                if (!response || typeof response !== 'object') {
                    paymentProcessing.value = false;
                    paymentError.value = $t('perfil_sga.error_pago');
                    return;
                }

                if (response.challengeUrl) {
                    // 3D Secure challenge needed
                    paymentChallengeUrl.value = response.challengeUrl;
                    // Start polling for payment completion
                    startPaymentCheck();
                } else if (response.compraFinalizada) {
                    // Payment completed without 3D Secure
                    completePayment();
                } else {
                    // Respuesta válida pero sin estado reconocido: no dejar el
                    // spinner activo indefinidamente. Se detiene el
                    // procesamiento y se muestra un error accionable.
                    paymentProcessing.value = false;
                    paymentError.value = $t('perfil_sga.error_pago');
                }
            })
            .catch(error => {
                paymentProcessing.value = false;
                stopPaymentCheck(); // Stop any active polling on payment failure
                const errorCode = error.response?.data?.response?.errorCode;
                const errorMap = {
                    102: 'Operación no permitida por tipo de tarjeta',
                    105: 'Crédito insuficiente',
                    108: 'Error en el número de tarjeta',
                    109: 'Error en la fecha de caducidad',
                    110: 'Error en los datos',
                    111: 'Bloque CVC2 incorrecto',
                    133: 'Error al procesar el pago, inténtelo de nuevo'
                };
                paymentError.value = errorMap[errorCode] || $t('perfil_sga.error_pago');
            });
    };

    /**
     * Poll backend for payment completion (used during 3D Secure).
     */
    const startPaymentCheck = () => {
        if (paymentCheckInterval.value) {
            return;
        }

        paymentCheckAttempts.value = 0;
        paymentCheckInterval.value = setInterval(() => {
            checkPaymentCompletion();
        }, 3000);
    };

    /**
     * Stop polling for payment completion.
     */
    const stopPaymentCheck = () => {
        if (paymentCheckInterval.value) {
            clearInterval(paymentCheckInterval.value);
            paymentCheckInterval.value = null;
        }
    };

    /**
     * Check if payment has been completed (after 3D Secure).
     */
    const checkPaymentCompletion = () => {
        if (paymentCheckInFlight.value) {
            return;
        }

        paymentCheckInFlight.value = true;

        const params = {
            filters: {},
            session_id: paymentData.value.session_id,
            tipo_pedido: 'sga'
        };

        $store.dispatch($config.getVuexAlias('front.get-pedido-procesado-paycomet'), params)
            .then(res => {
                // Operación cancelada mientras la consulta estaba en vuelo:
                // ignorar la respuesta (el carrito ya no existe).
                if (paymentCancelled.value) {
                    return;
                }

                // Extract tpv_order from polling response if available
                if (res.data?.n_pedido) {
                    paymentData.value.tpv_order = res.data.n_pedido;
                }

                if (!res.data.error) {
                    // Payment successful
                    stopPaymentCheck();
                    completePayment();
                } else {
                    // Aún en proceso: comprobar tope de intentos.
                    paymentCheckAttempts.value += 1;
                    if (paymentCheckAttempts.value >= MAX_POLLING_ATTEMPTS) {
                        stopPaymentCheck();
                        paymentProcessing.value = false;
                        paymentError.value = $t('perfil_sga.error_pago_tiempo');
                    }
                }
            })
            .catch(() => {
                // Error de red: no reintentar indefinidamente, cuenta como intento.
                paymentCheckAttempts.value += 1;
                if (paymentCheckAttempts.value >= MAX_POLLING_ATTEMPTS) {
                    stopPaymentCheck();
                    paymentProcessing.value = false;
                    paymentError.value = $t('perfil_sga.error_pago_tiempo');
                }
            })
            .finally(() => {
                paymentCheckInFlight.value = false;
            });
    };

    /**
     * Complete payment: fetch summary and show success state inside modal.
     */
    const completePayment = () => {
        // Si la operación fue cancelada mientras se resolvía el resumen, no
        // mostrar éxito para un carrito que ya no existe.
        if (paymentCancelled.value) {
            return;
        }
        stopPaymentCheck();
        loading.value = true;

        const params = {
            filters: {},
            session_id: paymentData.value.session_id,
            tpv_order: paymentData.value.tpv_order
        };

        $store.dispatch($config.getVuexAlias('sga.cart.resumen'), params)
            .then(res => {
                if (paymentCancelled.value) {
                    return;
                }
                successData.value = res.data?.data || null;
                paymentState.value = 'success';
            })
            .catch(() => {
                if (paymentCancelled.value) {
                    return;
                }
                // Fallback: show basic success without summary
                successData.value = {
                    tipo: 'renovacion',
                    tpv_order: paymentData.value.tpv_order,
                    importe_total: paymentData.value.total_price,
                    importe_total_sin_descuento: paymentData.value.total_price
                };
                paymentState.value = 'success';
            })
            .finally(() => {
                loading.value = false;
            });
    };

    /**
     * Cancel payment: delete cart and close modal.
     */
    const cancelPayment = () => {
        // Guard: el botón X dispara close() (→ closeModal) y closed (→
        // onModalClosed) en el mismo cierre; solo el primer DELETE debe
        // ejecutarse. Si ya hay una cancelación en curso, se devuelve la
        // misma promesa (idempotente) para que el segundo llamador pueda
        // encadenar .catch() sin TypeError.
        if (paymentCancelled.value && loading.value && cancelInFlight) {
            return cancelInFlight;
        }

        // Cancelar el autoenvío Redsys pendiente ANTES de borrar el carrito:
        // si el usuario cierra dentro de los 100ms, el POST al TPV no debe
        // competir con el DELETE.
        if (redsysSubmitTimeout.value) {
            clearTimeout(redsysSubmitTimeout.value);
            redsysSubmitTimeout.value = null;
        }
        redsysCancelled.value = true;
        paymentCancelled.value = true;
        // Detener el polling 3DS: una consulta en vuelo no debe completar el
        // pago después de cancelar (checkPaymentCompletion ignora respuestas
        // con paymentCancelled).
        stopPaymentCheck();

        loading.value = true;

        // Se retorna la promesa: el modal la encadena para reabrirse si el
        // borrado falla (cierre externo) o para no duplicar el manejo de error.
        cancelInFlight = $store.dispatch(
            $config.getVuexAlias('sga.cart.eliminar-datos'),
            { cart_id: paymentData.value.cart_id }
        )
            .then(() => {
                $toast.success($t('perfil_sga.carrito_cancelado'));
                loading.value = false;
                // Limpiar el cart_id: cancelOnUnmount (que corre al desmontar
                // tras emit('close')) no debe lanzar un segundo DELETE.
                paymentData.value.cart_id = null;
                $emit('close');
            })
            .catch(err => {
                // Si el borrado falla, el carrito sigue activo: NO cerrar el
                // modal (el usuario no tendría pantalla para reintentar).
                // Se muestra el error y el botón de cancelar queda disponible
                // para reintentar la cancelación.
                loading.value = false;
                paymentError.value = $t('perfil_sga.error_cancelar_carrito');
                throw err;
            })
            .finally(() => {
                cancelInFlight = null;
            });
        return cancelInFlight;
    };

    /**
     * Cancelación al desmontar (cierre por ESC/clic exterior): sin toast ni
     * emit('close') — el modal ya se está cerrando. Detiene el polling y el
     * autoenvío pendiente y borra el carrito si sigue sin finalizar.
     */
    const cancelOnUnmount = () => {
        stopPaymentCheck();
        if (redsysSubmitTimeout.value) {
            clearTimeout(redsysSubmitTimeout.value);
            redsysSubmitTimeout.value = null;
        }
        redsysCancelled.value = true;
        paymentCancelled.value = true;

        // Solo borrar si hay carrito pendiente (no en success) y la
        // cancelación no se ejecutó ya (cancelPayment limpió cart_id tras el
        // DELETE exitoso): evita el doble DELETE al desmontar.
        if (paymentState.value === 'payment' && paymentData.value.cart_id) {
            $store.dispatch(
                $config.getVuexAlias('sga.cart.eliminar-datos'),
                { cart_id: paymentData.value.cart_id }
            ).catch(() => {
                // Sin pantalla para reintentar: queda log + el carrito huérfano
                // lo limpia el job de expiración. No bloquear el cierre.
                console.warn('[useSgaRenovacionMembresiaPago] cancelOnUnmount: no se pudo borrar el carrito', paymentData.value.cart_id);
            });
        }
    };

    /**
     * Error handler: non-retryable vs retryable.
     */
    const handleError = err => {
        const status = err.response?.status;

        if (status === 503) {
            // Retryable: show retry message
            error.value = $t('perfil_sga.error_renovacion_reintentar');
        } else {
            // Non-retryable (400, 403, 422, network)
            error.value = $t('perfil_sga.error_renovacion');
            $toast.error(error.value);
        }
    };

    return {
        paymentState,
        paymentData,
        paymentError,
        paymentProcessing,
        paymentChallengeUrl,
        loading,
        error,
        cartData,
        successData,
        confirmRenovacion,
        handlePaymentRouting,
        handlePaycometPayment,
        handleRedsysPayment,
        handlePayToken,
        startPaymentCheck,
        stopPaymentCheck,
        checkPaymentCompletion,
        completePayment,
        cancelPayment,
        cancelOnUnmount,
        handleError
    };
}
